import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
	CircularLoader,
	CustomDialog,
	CustomIconButton,
	CustomTextField,
	icon,
	useFocus,
} from "../../../../../../common/components";
import styles from "./RedressAddActions.module.css";
import { translate } from "../../../../../../common/providers";
import { useApi } from "../../../../../../common/hooks";
import { RedressmentService } from "../../../../../../api";
import { setSelectedBlock } from "../../../../slice/document/document-slice";
import {
	cropImageFromOverlap,
	findPreviousContentCoordinates,
	getSelectionCoordinates,
} from "../../../../utils/coordinates-utils";
import { addContent, resetContent } from "../../../../slice/pdf/pdf-slice";
import { isNonEmptyArray } from "../../../../../../common/utils";
import { ROLES } from "../../../../constants/constants";

export default function RedressAddActions({
	addActionRef,
	isOverlapping,
	positionStyle,
	selectionRectangleRef,
	onCloseAddRedressment,
	onCloseSelection,
}) {
	const [open, setOpen] = useState(false);
	const [value, setValue] = useState("");
	const [coordinates, setCoordinates] = useState(null);
	const [loadingOCR, setLoadingOCR] = useState(false);
	const [croppedImage, setCroppedImage] = useState(null);
	const roles = useSelector(({ srDocument }) => srDocument?.roles);
	const docId = useSelector(({ srDocument }) => srDocument.documentId);
	const pageDimensions = useSelector(({ srDocument }) => srDocument.pageDimensions);
	const content = useSelector(({ srPdf }) => srPdf.content);
	const zoom = useSelector(({ srPdf }) => srPdf.zoom);
	const { call: extractOcrText } = useApi(RedressmentService.extractOcrText);
	const { call: addNewInformationContent } = useApi(RedressmentService.addNewInformationContent);
	const { unfocus } = useFocus();
	const dispatch = useDispatch();
	useEffect(() => {
		if (!open) {
			return;
		}
		if (!selectionRectangleRef.current) {
			return;
		}
		const best = getSelectionCoordinates({ zoom, selectionRectangleRef, pageDimensions });
		if (best) {
			const { x1, y1, x2, y2, page } = best;
			const selectedCoords = {
				pageId: page,
				x1: x1 / pageDimensions[page - 1].width,
				y1: y1 / pageDimensions[page - 1].height,
				x2: x2 / pageDimensions[page - 1].width,
				y2: y2 / pageDimensions[page - 1].height,
			};
			setCoordinates(selectedCoords);
			setLoadingOCR(true);
			setCroppedImage(cropImageFromOverlap(`image_${page - 1}`, selectionRectangleRef));
			extractOcrText({ docId }, selectedCoords)
				.then((data) => {
					setValue(data?.value);
				})
				.catch(console.error)
				.finally(() => {
					setLoadingOCR(false);
				});
		}
	}, [open, selectionRectangleRef, zoom, pageDimensions, extractOcrText, docId]);
	const handleOpenAddDialog = (e) => {
		e.preventDefault();
		e.stopPropagation();
		setOpen((prev) => !prev);
	};
	const handleClose = () => {
		setCoordinates(null);
		onCloseSelection();
		onCloseAddRedressment();
		unfocus();
	};
	const handleChange = (e) => {
		setValue(e?.target?.value);
	};
	const handleSubmit = () => {
		if (value?.length > 0) {
			const payload = {
				reqRecPosDto: coordinates,
				previousId: null,
				text: value,
			};
			const targetContent = content[coordinates?.pageId - 1]?.redr;
			let previousItem = null;
			if (targetContent) {
				previousItem = findPreviousContentCoordinates(targetContent, coordinates);
				if (previousItem) {
					payload.previousId = previousItem.contentId;
				}
			}
			if (!previousItem && coordinates?.pageId > 1) {
				const previousPageTargetContent = content[coordinates?.pageId - 2]?.redr;
				if (isNonEmptyArray(previousPageTargetContent)) {
					payload.previousId = previousPageTargetContent[previousPageTargetContent.length - 1].contentId;
				}
			}
			addNewInformationContent({ docId }, payload)
				.then((data) => {
					dispatch(addContent(data));
					if (roles.includes(ROLES.REVIEWER.name.toUpperCase()) && coordinates.pageId) {
						dispatch(resetContent({ pages: [coordinates.pageId], mode: "req" }));
					}
					if (roles.includes(ROLES.VALIDATOR.name.toUpperCase()) && coordinates.pageId) {
						dispatch(resetContent({ pages: [coordinates.pageId], mode: "val" }));
					}
				})
				.catch(console.error)
				.finally(() => {
					handleClose();
					dispatch(setSelectedBlock(null));
				});
		}
	};
	return (
		<>
			<div
				ref={addActionRef}
				className={styles.container}
				data-is-warning={isOverlapping}
				style={(!isOverlapping && { ...positionStyle }) || {}}
			>
				{(isOverlapping && (
					<div className={styles.warningMessage}>
						<div>{translate("smart-review.redress-overlay.warning")}</div>
						<CustomIconButton btnClassName={styles.button} icon={icon.faXmark} onClick={onCloseSelection} />
					</div>
				)) || (
					<>
						<CustomIconButton
							btnClassName={styles.button}
							icon={icon.faCheck}
							onClick={handleOpenAddDialog}
						/>
						<CustomIconButton btnClassName={styles.button} icon={icon.faXmark} onClick={onCloseSelection} />
					</>
				)}
			</div>
			<CustomDialog
				disableBackdropClick
				disableEscapeKeyDown
				className={styles.dialog}
				open={open}
				submitDisabled={value?.length < 1}
				subTitle={
					<div className={styles.contentContainer}>
						<img alt="cropped_image" className={styles.croppedImage} src={croppedImage} />
						<div className={styles.information}>
							{translate("smart-review.redress-add-dialog.paragraph")}
						</div>
						{(loadingOCR && (
							<div className={styles.loading}>
								<CircularLoader />
							</div>
						)) || (
							<CustomTextField
								autoFocus
								fullWidth
								multiline
								className={styles.textField}
								rows={5}
								size="small"
								value={value}
								variant="outlined"
								onChange={handleChange}
								onFocus={(e) => {
									const len = e.target.value.length;
									e.target.setSelectionRange(len, len);
								}}
							/>
						)}
					</div>
				}
				title={translate("smart-review.redress-add-dialog.title")}
				onClose={handleClose}
				onSubmit={handleSubmit}
			/>
		</>
	);
}
